import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, Camera, Users } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import { EventStatusBadge } from '@/components/events/EventStatusBadge';
import type { EventListItem } from '@/api/types/event.types';

interface EventCardProps {
  event: EventListItem;
}

export function EventCard({ event }: EventCardProps) {
  const { t } = useTranslation();

  const formattedDate = (() => {
    try {
      return format(parseISO(event.event_date), 'PPP');
    } catch {
      return event.event_date;
    }
  })();

  return (
    <Link
      to={`/events/${event.id}`}
      className="group block rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all hover:border-primary-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-600"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 dark:text-gray-100 dark:group-hover:text-primary-400">
          {event.title}
        </h3>
        <EventStatusBadge status={event.processing_status} />
      </div>

      {event.partners_display && (
        <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
          <Users className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{event.partners_display}</span>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{formattedDate}</span>
        </div>

        {event.venue_name && (
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{event.venue_name}</span>
          </div>
        )}

        <div className="flex items-center gap-1.5">
          <Camera className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            {event.photo_count} {t('events.photos')}
            {event.processed_count > 0 && (
              <span className="text-gray-400 dark:text-gray-500">
                {' '}
                ({event.processed_count} {t('events.processed')})
              </span>
            )}
          </span>
        </div>
      </div>
    </Link>
  );
}
